import { DataService } from '../shared/network/data.service';
import { FancyNumberPipe } from "../shared/directives/fancy-number.pipe";
import { BalanceCard } from "../shared/project/cards/balance-card";
import { AltCardView, Card } from "../shared/project/cards/card";
import { VarianceModel } from "../shared/project/cards/cards-model";
import { DriverType } from "../shared/project/driver-types/driver-type";
import { CardHelper } from "./cardHelper";
import { WaterfallItem2 } from "./waterfall-item";
import { CustomLinkModule } from "../shared/quill/quill-custom-link-module";

export class BaseCardsComponent {
    blockHeight: number = 42;
    
    public varianceModel: VarianceModel

    showWaterfall: boolean = true;
    seqMode: boolean = true;
    showOutOfScope: boolean = false;
    
    oldOutcomeHeight: number = 0;
    newOutcomeHeight: number = 0;
    newOutcomeHeightBeforeOutOfScope: number = 0;
    newOutcomeHeightAfterOutOfScope: number = 0;
    
    totalOutcomeCtx = FancyNumberPipe.createDefaultContext();
    cardOutcomeCtx = FancyNumberPipe.createDefaultContext();
    cardPercCtx = {
        roundTo: 1,
        scale: undefined,
        showPlusForPositive: true,
        use2stepRounding: false,
    };
    
    // Quill (story editor) setup shared by the cards and project-overview screens
    private static readonly QUILL_TOOLBAR_VISIBLE_KEY = 'quillToolbarVisible';
    public showQuillToolbar = localStorage.getItem(BaseCardsComponent.QUILL_TOOLBAR_VISIBLE_KEY) === 'true';
    public quillModules = this.createQuillModules(true);
    protected quillInstance: any;

    protected createQuillModules(withUpdateButton: boolean) {
        return {
            toolbar: {
              container: [
                ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
                //['blockquote', 'code-block'],

                [{ 'align': [] }],
                //[{ 'header': 1 }, { 'header': 2 }],               // custom button values
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                [{ 'script': 'sub'}, { 'script': 'super' }],      // superscript/subscript
                [{ 'indent': '-1'}, { 'indent': '+1' }],          // outdent/indent

                [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

                [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
                //[{ 'font': [] }],

                ['clean'],                                         // remove formatting button

                ['link'/*, 'image', 'video'*/],                    // link and image, video

                withUpdateButton ? ['format', 'update'] : ['format'],   // custom right-aligned buttons
              ],
              handlers: {
                format: () => this.toggleQuillToolbar(),
                update: () => this.onUpdateStory(),
              },
            },
            customLink: new CustomLinkModule(),
        };
    }

    public toggleQuillToolbar() {
        this.showQuillToolbar = !this.showQuillToolbar;
        localStorage.setItem(BaseCardsComponent.QUILL_TOOLBAR_VISIBLE_KEY, String(this.showQuillToolbar));
    }

    /** The "update" toolbar button (AI story regeneration); screens that support it override this. */
    /**
     * Long-polls a story endpoint in slices: the server answers within its slice with { story, isBeingLoaded } and we ask again while the story
     * is still being generated, so no single request outlives a load balancer's idle timeout. The server reports a story as being loaded only
     * while its job is provably in flight (a database marker that goes stale by itself), so the cap below is a safety net, not the normal end.
     */
    protected async pollStory(dataService: DataService, url: string): Promise<string | null> {
        for (let slice = 0; slice < BaseCardsComponent.maxStoryPollSlices; ++slice) {
            const res = await dataService.get<any>(url);
            if (!res.isBeingLoaded) {
                return res.story;
            }
        }
        console.warn(`Gave up polling the story after ${BaseCardsComponent.maxStoryPollSlices} slices: ${url}`);
        return null;
    }
    private static readonly maxStoryPollSlices = 36;    //50 s slices: 30 minutes

    public onUpdateStory() {
    }

    public onQuillCreated($event: any) {
        this.quillInstance = $event;
    }

    protected unfocusQuill() {
        if (this.quillInstance) {
            this.quillInstance.blur();
        }
    }

    private shouldShowOutOfScope() {
        return this.showOutOfScope && this.varianceModel.outOfScopeCards && this.varianceModel.outOfScopeCards.length;
    }

    protected calcOutcomeCtxs() {
        this.totalOutcomeCtx = CardHelper.calcOutcomeFancyNumContext(this.varianceModel.cards, [ this.varianceModel.oldTotalOutcome, this.varianceModel.newTotalOutcome ]);
        this.cardOutcomeCtx = CardHelper.calcOutcomeFancyNumContext(this.varianceModel.cards, [ this.varianceModel.oldTotalOutcome, this.varianceModel.newTotalOutcome ]);
        this.cardOutcomeCtx.showPlusForPositive = true;
    }

    /**
     * Creates the values that are needed in the template to draw the
     * sequential (seq) and absolute (abs) bars of the waterfall diagram.
     *
     * IMPORTANT:
     *   • All calculations are done “bottom up”, i.e. we use CSS  bottom  instead
     *     of  top .
     *   • The whole diagram is scaled so that its height never exceeds 125 px.
     *   • The WaterfallItem that is written back to every card is an instance of
     *     WaterfallItem2 (see class definition below).
     *   • Colours are fixed: ‘#5C5C5C’ for positive values, ‘#DE3F3F’ for
     *     negatives – this applies to both seq- and abs-bars.
     *   • After the calculation every bar that would be smaller than 1 px is
     *     forced to 1 px so that it is always visible.
     *
     * @param oldOutcome            start value (left hand total column)
     * @param newOutcome            end   value (right hand total column)
     * @param cards                 drivers that have to be visualised
     * @param balanceCard           (optional) “balance” line shown after drivers
     * @param outOfScopeCards       (optional) drivers that may be hidden
     * @param outcomeAfterOutOfScope total after the out-of-scope drivers
     */
    loadWaterfallItems(
        oldOutcome: number,
        newOutcome: number,
        cards: Card[],
        balanceCard: BalanceCard | null,
        outOfScopeCards: Card[],
        outcomeAfterOutOfScope: number
    ) {
        const MAX_HEIGHT = 125;             // px
        const POS_COLOUR = '#5C5C5C';
        const NEG_COLOUR = '#DE3F3F';

        let minVal = oldOutcome;
        let maxVal = oldOutcome;
        let curVal = oldOutcome;

        const updateMinMax = (v: number) => {
            if (v < minVal) {
                minVal = v;
            }
            if (v > maxVal) {
                maxVal = v;
            }
        };

        const seqDiffFor = (c: Card) => c.seqDriverValue;
        const absDiffFor = (c: Card) => c.absDriverValue;

        cards.forEach(card => {
            const seq = seqDiffFor(card);
            const abs = absDiffFor(card);

            if (!this.seqMode) {
                updateMinMax(curVal + abs);     // possible absolute extremum
            }
            updateMinMax(curVal + seq);     // value after sequential diff
            curVal += seq;                  // continue with next base line
        });

        outOfScopeCards.forEach(card => {
            const seq = seqDiffFor(card);
            const abs = absDiffFor(card);
            if (!this.seqMode) {
                updateMinMax(curVal + abs);
            }
            updateMinMax(curVal + seq);
            curVal += seq;
        });

        if (balanceCard) {
            const balSeq = balanceCard.seqValue;
            updateMinMax(curVal + balSeq);
            curVal += balSeq;
        }

        updateMinMax(newOutcome);
        updateMinMax(outcomeAfterOutOfScope);

        let range = maxVal - minVal;
        if (range === 0) {
            range = 1;
        }
        const pxPerUnit = MAX_HEIGHT / range;

        /*console.log('loadWaterfallItems5 - Range Calculation:', {
            seqMode: this.seqMode,
            oldOutcome,
            newOutcome,
            outcomeAfterOutOfScope,
            cards: cards.map(c => ({ seq: c.seqDriverValue, abs: c.absDriverValue })),
            outOfScopeCards: outOfScopeCards.map(c => ({ seq: c.seqDriverValue, abs: c.absDriverValue })),
            balanceCard: balanceCard ? { 
                seqValue: balanceCard.seqValue,
                chartDiff: balanceCard.chartDiff,
                chartDiffWithOutOfScope: balanceCard.chartDiffWithOutOfScope
            } : null,
            minVal,
            maxVal,
            range,
            pxPerUnit: MAX_HEIGHT / (range || 1)
        });*/

        function fillWaterfallItem(card: Card) {
            const seqDiff = seqDiffFor(card);
            const absDiff = absDiffFor(card);

            const baselinePx = (curVal - minVal) * pxPerUnit;       // shared “zero” of this column
            const seqHeight = Math.max(1, Math.round(Math.abs(seqDiff * pxPerUnit)));
            const absHeight = Math.max(1, Math.round(Math.abs(absDiff * pxPerUnit)));

            const seqBottom = seqDiff >= 0 ? Math.round(baselinePx) : Math.round(baselinePx - seqHeight);
            const absBottom = absDiff >= 0 ? Math.round(baselinePx) : Math.round(baselinePx - absHeight);

            const item = new WaterfallItem2();

            item.seqColor   = seqDiff >= 0 ? POS_COLOUR : NEG_COLOUR;
            item.seqHeight  = seqHeight;
            item.seqBottom  = seqBottom;

            item.absColor   = absDiff >= 0 ? POS_COLOUR : NEG_COLOUR;
            item.absHeight  = absHeight;
            item.absBottom  = absBottom;

            card.waterfallItem = item;

            curVal += seqDiff;
        }

        curVal = oldOutcome;                 // reset baseline
        cards.forEach(card => {
            fillWaterfallItem(card);
        });

        if (balanceCard) {
            const seqDiff = balanceCard.seqValue;
            const baselinePx = (curVal - minVal) * pxPerUnit;
            const seqHeight = Math.max(1, Math.round(Math.abs(seqDiff * pxPerUnit)));
            const seqBottom = seqDiff >= 0 ? Math.round(baselinePx) : Math.round(baselinePx - seqHeight);
            
            const item = new WaterfallItem2();
            item.seqColor = seqDiff >= 0 ? POS_COLOUR : NEG_COLOUR;
            item.seqHeight = seqHeight;
            item.seqBottom = seqBottom;

            balanceCard.waterfallItem = item;
            curVal += balanceCard.seqValue;
        }
        
        this.newOutcomeHeightBeforeOutOfScope = Math.max(1, Math.round((curVal - minVal) * pxPerUnit));

        outOfScopeCards.forEach(card => {
            fillWaterfallItem(card);
        });

        this.newOutcomeHeightAfterOutOfScope = Math.max(1, Math.round((curVal - minVal) * pxPerUnit));

        this.oldOutcomeHeight = Math.max(1, Math.round((oldOutcome - minVal) * pxPerUnit));
        this.newOutcomeHeight = Math.max(1, Math.round((newOutcome - minVal) * pxPerUnit));
    }

    public cardMouseEnter(card: Card) {
        card.cardHovered = true;
    }
    public cardMouseLeave(card: Card) {
        card.cardHovered = false;
    }
    
    hasFormCompFromTo(altView: AltCardView): boolean {
        return CardHelper.hasFormCompFromTo(altView);
    }

    areBenchmarksAvailable(card: Card): boolean {
        return card.driverType != DriverType.new && card.driverType != DriverType.lost && card.driverType != DriverType.mix;
    }

}